import { useCallback } from "react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isSlotWithinOperatingHours } from "@/components/booking/utils/timeSlotUtils";

interface EventHandlersProps {
  isLoggedIn: boolean;
  setSelectedDate: (date: Date) => void;
  setSelectedTimeSlot: (timeSlot: string) => void;
  setIsBookingOpen: (isOpen: boolean) => void;
  setSelectedBooking: (booking: any) => void;
  setBookingAction: (action: "edit" | "cancel" | null) => void;
  setIsBookingActionOpen: (isOpen: boolean) => void;
  currentUserId: string | null;
  setIsBookingDialogOpen: (isOpen: boolean) => void;
  isServiceOwner?: boolean;
  isAdmin?: boolean;
}

export const useEventHandlers = ({
  isLoggedIn,
  setSelectedDate,
  setSelectedTimeSlot,
  setIsBookingOpen,
  setSelectedBooking,
  setBookingAction,
  setIsBookingActionOpen,
  currentUserId,
  setIsBookingDialogOpen,
  isServiceOwner = false,
  isAdmin = false
}: EventHandlersProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleDateClick = useCallback((info: any) => {
    if (!isLoggedIn) {
      toast({
        title: "Authentication Required",
        description: "Please log in to book an appointment",
        variant: "destructive"
      });
      return;
    }

    // Extract date and time from FullCalendar's dateStr to avoid timezone conversion issues
    const clickedDateStr = info.dateStr; // e.g., "2025-09-19T17:30:00"
    
    // Parse the datetime string to extract date and time components
    let selectedDate: Date;
    let timeSlot: string;
    
    if (clickedDateStr.includes('T')) {
      // Handle datetime string (e.g., "2025-09-19T17:30:00")
      const [datePart, timePart] = clickedDateStr.split('T');
      const [year, month, day] = datePart.split('-').map(Number);
      const [hour, minute] = timePart.split(':').map(Number);
      
      // Create date in local timezone without any conversion
      selectedDate = new Date(year, month - 1, day, hour, minute);
      timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    } else {
      // Handle date-only string (e.g., "2025-09-19")
      const [year, month, day] = clickedDateStr.split('-').map(Number);
      selectedDate = new Date(year, month - 1, day);
      timeSlot = '09:00'; // Default to 9 AM for date-only clicks
    }
    
    const currentDate = new Date();
    
    // Enhanced debug logging to track time processing
    console.log('🕒 Calendar click debug:', {
      originalDateStr: clickedDateStr,
      parsedDate: selectedDate.toLocaleString(),
      timeSlot: timeSlot,
      currentDate: currentDate.toLocaleString()
    });
    
    if (selectedDate < currentDate) {
      toast({
        title: "Invalid Selection",
        description: "Cannot book appointments in the past",
        variant: "destructive"
      });
      return;
    }

    // Check if the selected time is within operating hours
    const operatingHours = info.view.calendar.getOption('businessHours');
    
    if (!isSlotWithinOperatingHours(timeSlot, selectedDate, operatingHours)) {
      toast({
        title: "Outside Operating Hours",
        description: "Please select a time during business hours",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedDate(selectedDate);
    console.log('🔧 Setting selectedTimeSlot to:', timeSlot, 'from local date:', selectedDate.toLocaleString());
    setSelectedTimeSlot(timeSlot);
    setIsBookingOpen(true);
  }, [isLoggedIn, setSelectedDate, setSelectedTimeSlot, setIsBookingOpen, toast]);

  const handleEventClick = useCallback((info: any) => {
    if (!info.event.extendedProps?.booking) {
      return;
    }

    const eventData = info.event.extendedProps;
    console.log("📅 Event data comparison:", {
      booking: eventData.booking,
      storedBookingTime: eventData.booking?.booking_time,
      eventStartTime: info.event.start?.toISOString(),
      eventStartLocal: info.event.start?.toLocaleString(),
      timezoneOffset: new Date().getTimezoneOffset(),
      // Compare the displayed time vs click time
      displayedTime: eventData.booking?.booking_time,
      clickedTime: info.event.start?.toISOString()
    });

    const bookingStatus = eventData.booking?.status;
    console.log("📊 Booking status:", bookingStatus);
    
    if (bookingStatus === 'blocked') {
      // Only prevent interaction for non-owners/non-admins
      if (!isServiceOwner && !isAdmin) {
        toast({
          title: "Blocked time slot",
          description: "This time slot is blocked and cannot be booked",
        });
        return;
      }
      // For service owners/admins, allow clicking blocked slots to manage them
    }
    
    if (bookingStatus === 'travel') {
      // Only prevent interaction for non-owners/non-admins
      if (!isServiceOwner && !isAdmin) {
        toast({
          title: "Travel time",
          description: "This is travel time between bookings",
        });
        return;
      }
      // For service owners/admins, allow clicking travel slots to manage them
    }
    
    // Check if this booking can be managed by the current user
    if (currentUserId && (
      eventData.booking?.customer_id === currentUserId ||  // Customer's own booking
      isServiceOwner ||  // Service owner can manage any booking
      isAdmin  // Admin can manage any booking
    )) {
      console.log("✅ User can manage booking:", {
        booking: eventData.booking,
        isCustomer: eventData.booking?.customer_id === currentUserId,
        isServiceOwner,
        isAdmin,
        currentUserId,
        bookingCustomerId: eventData.booking?.customer_id
      });
      
      // Navigate to booking details page instead of opening dialog
      navigate(`/booking/${eventData.booking.id}/details`);
    } else {
      if (!currentUserId) {
        console.log("❌ No current user ID");
        toast({
          title: "Please log in",
          description: "You need to log in to manage bookings",
        });
      } else {
        console.log("❌ User cannot manage this booking:", {
          currentUserId,
          bookingCustomerId: eventData.booking?.customer_id,
          isServiceOwner,
          isAdmin
        });
        toast({
          title: "Not your booking",
          description: "You can only manage your own bookings",
        });
      }
    }
  }, [toast, currentUserId, navigate, isServiceOwner, isAdmin]);

  return {
    handleDateClick,
    handleEventClick
  };
};
